import React, { useState, useEffect, useRef } from 'react';
import useGenerateText from '../../hooks/useGenerateText';
import DraftEditor, { DraftEditorHandle } from './DraftEditor';

interface QuestionVariations {
  singular: string;
  plural: string;
}

interface QuestionSimpleEditorProps {
  content: string | QuestionVariations;
  onChange: (content: QuestionVariations) => void;
  isEditable: boolean;
  onEditableChange: (editable: boolean) => void;
  showEditableToggle?: boolean;
}

const QuestionSimpleEditor: React.FC<QuestionSimpleEditorProps> = ({ content, onChange, isEditable, onEditableChange, showEditableToggle = true }) => {
  const [activeTab, setActiveTab] = useState<keyof QuestionVariations>('singular');
  const { generateText, isGenerating, error, clearError } = useGenerateText();

  // Ref for single Draft editor
  const editorRef = useRef<DraftEditorHandle | null>(null);
  
  // Utility function to fill empty plural with singular content
  const fillEmptyVariationsWithSingular = (variations: QuestionVariations): QuestionVariations => {
    const { singular, plural } = variations;

    // Check if text is empty (blank or just whitespace)
    const isEmpty = (text: string) => !text || text.trim() === '';

    // If singular has content but plural is empty, fill it
    if (!isEmpty(singular) && isEmpty(plural)) {
      return {
        ...variations,
        plural: singular
      };
    }

    return variations;
  };
  
  // Initialize variations from content prop
  const [variations, setVariations] = useState<QuestionVariations>(() => {
    let initialVariations: QuestionVariations;

    if (typeof content === 'string') {
      // Legacy format - string directly -> use as singular
      initialVariations = {
        singular: content,
        plural: ''
      };
    } else if (content && typeof content === 'object') {
      // Check if it's the new format with singular/plural
      if ('singular' in content) {
        const variationContent = content as QuestionVariations;
        initialVariations = {
          singular: variationContent.singular || '',
          plural: variationContent.plural || ''
        };
      }
      // Legacy format with original/tuSingular/etc
      else if ('original' in content) {
        const legacyContent = content as any;
        initialVariations = {
          singular: legacyContent.original || legacyContent.tuSingular || '',
          plural: legacyContent.tuPlural || ''
        };
      }
      // Try to find text in various other legacy formats
      else {
        let textValue = '';
        const contentObj = content as any;

        // Check for nested content structures
        if (contentObj.content) {
          if (typeof contentObj.content === 'string') {
            textValue = contentObj.content;
          } else if (contentObj.content.text) {
            textValue = contentObj.content.text;
          } else if (contentObj.content.questionText) {
            textValue = contentObj.content.questionText;
          }
        }

        // Check for other common field names
        if (!textValue) {
          textValue = contentObj.questionText || contentObj.text || contentObj.textContent ||
                      contentObj.value || contentObj.name || contentObj.title || '';
        }

        initialVariations = {
          singular: textValue,
          plural: ''
        };
      }
    } else {
      // Default empty state
      initialVariations = {
        singular: '',
        plural: ''
      };
    }

    // Auto-fill empty plural with singular content if applicable
    return fillEmptyVariationsWithSingular(initialVariations);
  });

  // Update parent whenever variations change (only call onChange when variations actually change)
  const prevVariationsRef = useRef<QuestionVariations | null>(null);
  
  useEffect(() => {
    // Only call onChange if variations actually changed
    if (!prevVariationsRef.current ||
        prevVariationsRef.current.singular !== variations.singular ||
        prevVariationsRef.current.plural !== variations.plural) {
      onChange(variations);
      prevVariationsRef.current = { ...variations };
    }
  }, [variations, onChange]);

  const tabs = [
    { key: 'singular' as keyof QuestionVariations, label: 'Singular' },
    { key: 'plural' as keyof QuestionVariations, label: 'Plural' }
  ];

  // Update editor content when switching tabs
  const prevActiveTab = useRef(activeTab);
  useEffect(() => {
    // Only update when tab changes, not when variations change
    if (prevActiveTab.current !== activeTab && editorRef.current) {
      editorRef.current.setHTML(variations[activeTab]);
      prevActiveTab.current = activeTab;
    }
  }, [activeTab, variations]);

  const handleContentChange = (value: string) => {
    setVariations(prev => {
      const updated = {
        ...prev,
        [activeTab]: value
      };

      // If we're editing the singular tab and plural is empty, auto-fill it
      if (activeTab === 'singular') {
        return fillEmptyVariationsWithSingular(updated);
      }

      return updated;
    });
  };

  const handleAutoGenerate = async () => {
    // Check if we have singular text to generate from
    if (!variations.singular || variations.singular.trim() === '') {
      alert('Por favor, ingresa primero el texto singular antes de autogenerar el plural.');
      return;
    }

    // Only generate for plural tab
    if (activeTab === 'singular') {
      return;
    }

    clearError();

    try {
      // Always use tuPlural for generating plural variations
      const generatedText = await generateText(variations.singular, 'tuPlural');

      // Update the plural variation with the generated text
      setVariations(prev => ({
        ...prev,
        plural: generatedText
      }));

      // Also update the Draft editor directly
      if (editorRef.current) {
        editorRef.current.setHTML(generatedText);
      }

    } catch (error) {
      // Error is already handled by the hook
      console.error('Error generating text:', error);
    }
  };

  return (
    <div className="mt-4 pt-2">
      <label className="form-label">Texto de la pregunta</label>
      <small className="text-muted d-block mb-3">
        Configura el texto de la pregunta en singular y plural.
      </small>
      
      {/* Tab Navigation */}
      <ul className="nav nav-tabs mb-0">
        {tabs.map(tab => (
          <li key={tab.key} className="nav-item">
            <button
              className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
              type="button"
              style={{ color: '#000' }}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {/* Editor for active tab */}
      <div
        className="border border-top-0"
        style={{
          borderRadius: '0 0 0.375rem 0.375rem'
        }}
      >
        <DraftEditor
          ref={editorRef}
          defaultValue={variations[activeTab]}
          onChange={(html) => handleContentChange(html)}
          placeholder={`Escribe el texto de la pregunta para ${tabs.find(t => t.key === activeTab)?.label}...`}
          readOnly={false}
        />
      </div>

      {/* Auto-generate button */}
      {activeTab === 'plural' && (
        <div className="mt-4">
          <button
            type="button"
            className="btn btn-outline-dark"
            onClick={handleAutoGenerate}
            disabled={isGenerating || !variations.singular}
            title={!variations.singular ? 'Ingresa primero el texto singular' : 'Generar texto plural automáticamente basado en el singular'}
          >
            {isGenerating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Generando...
              </>
            ) : (
              <>
                <i className="bi bi-magic me-2"></i>
                Autogenerar plural
              </>
            )}
          </button>
          {!variations.singular && (
            <small className="text-warning ms-2">
              <i className="bi bi-exclamation-triangle me-1"></i>
              Ingresa el texto singular primero
            </small>
          )}
          {error && (
            <div className="alert alert-danger mt-2" role="alert">
              <i className="bi bi-exclamation-circle me-2"></i>
              {typeof error === 'string' ? error : 'Error al generar el texto'}
            </div>
          )}
        </div>
      )}

      {/* Editable Toggle - Moved to the end */}
      {showEditableToggle && (
        <div className="mt-4">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="questionEditable"
              checked={isEditable}
              onChange={(e) => onEditableChange(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="questionEditable" style={{ color: '#000' }}>
              Permitir edición por Arquitectos
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionSimpleEditor;